import { Component, forwardRef, Injector, OnInit, Optional, SkipSelf } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PHOENIX_CONTEXTS, PhxContextService } from 'phx-core';
import { IGroup } from '@app/admin/groups/admin-group.service';
import { AdminFeatureFlagService } from '@app/services/feature-flags/admin';
import { DebounceTimeConfig } from '@services/dynamic-configs/debounce-time-config';
import { GroupsMultiSelectFilterBaseComponent } from './base.component';

import 'style-loader!./local.component.deep.scss';

@Component({
  selector: 'groups-multi-select-filter',
  styleUrls: ['./base.component.scss'],
  templateUrl: './base.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GroupsMultiSelectFilterComponent),
      multi: true,
    },
  ],
  host: {
    'class': 'groups-multi-select-filter',
    '[class.phx-filter]': 'usedAsFilter && !phxImplementation',
    '[class.phoenix-styled-filter]': 'usedAsFilter && !phxImplementation',
    '[attr.tabindex]': 'usedAsFilter && !phxImplementation ? 0 : null',
  },
})
export class GroupsMultiSelectFilterComponent
  extends GroupsMultiSelectFilterBaseComponent
  implements OnInit, ControlValueAccessor {
  public usedAsFilter: boolean;
  private onChange: (selectedGroupIds: number[]) => void = () => { /* */ };

  constructor(
    private debounceTimeConfig: DebounceTimeConfig,
    injector: Injector,
    adminFeatureFlagService: AdminFeatureFlagService,
    @Optional() @SkipSelf() contextService: PhxContextService,
    ) {
    super(injector, adminFeatureFlagService);
    this.usedAsFilter = contextService?.getType() === PHOENIX_CONTEXTS.FILTER;
    this.overlayClassName = this.usedAsFilter ? 'phoenix-styled-filter-dropdown' : '';
  }

  public ngOnInit(): void {
    super.ngOnInit();
    const debounceTime: number = this.debounceTimeConfig.getDebounceTime();
    if (debounceTime) {
      this.debounceTime = debounceTime;
    }
  }

  public registerOnChange(fn: (selectedGroupIds: number[]) => void): void {
    this.onChange = ((selectedGroupIds: number[]) => {
      fn(selectedGroupIds);
    });
  }

  public registerOnTouched(): void { /** */ }

  public writeValue(parentSelectedGroupIds: number[] | string[]): void {
    if (!parentSelectedGroupIds) parentSelectedGroupIds = [];

    let numericIds: number[] = [];
    if (parentSelectedGroupIds.length > 0 && typeof parentSelectedGroupIds[0] === 'string') {
      numericIds = (parentSelectedGroupIds as string[]).map((id: string) => parseInt(id, 10));
    } else {
      numericIds = parentSelectedGroupIds as number[];
    }
    if (this.phxImplementation) this.selectedOptions = numericIds;
    else {
      this.parentSelectedGroupIds$.next(numericIds);
      this.selectedGroupIds$.next(new Set(numericIds));
    }
  }

  protected onSelectedGroupIdsUpdated(selectedGroupIds: Set<number>): void {
    if (!this.hasChanged) return;
    this.onChange(selectedGroupIds ? Array.from(selectedGroupIds) : []);
  }

  protected onGroupsUpdated(groups: IGroup[], parentSelectedGroupIds: Set<number>): void {
    this.options$.next(groups.map((group: IGroup) => this.transformGroupToOptions(group, parentSelectedGroupIds)));
  }
}